const getDefaultWindow = (): Window | undefined =>
  typeof window !== "undefined" ? window : undefined;

/**
 * Lê um parâmetro de query string da URL atual.
 *
 * O app de routing usa hash routing (createHashRouter), então a query string
 * (?id=...&dn-group-id=...&trip-id=...) fica DENTRO do hash, e não em
 * `window.location.search`.
 * Ex.: https://dev.latam-routing.rio.cloud/#/routing/trips/driver?id=<uuid>&dn-group-id=<uuid>&trip=true
 *
 * A função tenta primeiro a query string do hash e, como fallback (rotas que
 * usam BrowserRouter), a search "tradicional".
 *
 * @param name nome do parâmetro a ser lido (ex.: "id", "dn-group-id", "trip-id").
 * @param win janela usada para a leitura (injetável para testes; por padrão `window`).
 * @returns o valor quando presente e não vazio; caso contrário, `null`.
 */
export const getUrlQueryParam = (
  name: string,
  win: Window | undefined = getDefaultWindow()
): string | null => {
  if (!win) return null;

  const { hash, search } = win.location;
  const hashQueryIndex = hash.indexOf("?");
  const queryString = hashQueryIndex >= 0 ? hash.slice(hashQueryIndex + 1) : search;

  const value = new URLSearchParams(queryString).get(name);
  return value && value.trim().length > 0 ? value : null;
};

/**
 * Lê o parâmetro `id` da URL atual (usado como `route-id` nas requisições).
 *
 * @returns o id quando presente e não vazio; caso contrário, `null`.
 */
export const getRouteIdFromUrl = (): string | null => getUrlQueryParam("id");

/**
 * Lê o parâmetro `dn-group-id` da URL atual.
 *
 * @returns o dn-group-id quando presente e não vazio; caso contrário, `null`.
 */
export const getDnGroupIdFromUrl = (): string | null => getUrlQueryParam("dn-group-id");

export const getDnGroupNameFromUrl = (): string | null => getUrlQueryParam("dn-group-name");

/**
 * Lê o parâmetro `trip-id` da URL atual.
 *
 * Usado para abrir/identificar a viagem nas telas de detalhe (monitoramento e
 * desempenho) via deep-link, ex.:
 *   #/routing/map/trips?trip-id=<uuid>
 *   #/routing/performance/list/trip?tab=1&trip-id=<uuid>
 *   #/routing/trips/performance?tab=1&trip-id=<uuid>
 *
 * @param win janela usada para a leitura (injetável para testes; por padrão `window`).
 * @returns o trip-id quando presente e não vazio; caso contrário, `null`.
 */
export const getTripIdFromUrl = (win: Window | undefined = getDefaultWindow()): string | null =>
  getUrlQueryParam("trip-id", win);

/**
 * Lê o parâmetro `driver-id` da URL atual.
 *
 * Acompanha o `trip-id` nos deep-links da tela de detalhe de desempenho, onde a
 * busca dos detalhes da viagem precisa do id do motorista (rioId) além do tripId.
 *
 * @param win janela usada para a leitura (injetável para testes; por padrão `window`).
 * @returns o driver-id quando presente e não vazio; caso contrário, `null`.
 */
export const getDriverIdFromUrl = (win: Window | undefined = getDefaultWindow()): string | null =>
  getUrlQueryParam("driver-id", win);
